// ============================================================================
// PUSH NOTIFICATIONS UTILITY
// Path: src/utils/pushNotifications.js
// Purpose: Handle push notification subscriptions on the frontend
// ============================================================================

import { supabase } from '../lib/supabase'

/**
 * Subscribe user to push notifications
 * @param {string} userId - The user's ID
 * @returns {Promise<PushSubscription|null>} - The subscription object or null
 */
export async function subscribeToPushNotifications(userId) {
  try {
    // Check if service worker and push are supported
    if (!('serviceWorker' in navigator)) {
      console.log('⚠️ Service workers not supported')
      return null
    }

    if (!('PushManager' in window)) {
      console.log('⚠️ Push notifications not supported')
      return null
    }

    console.log('📱 Registering service worker...')

    // Register service worker
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    })

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready

    console.log('✅ Service worker registered')

    // Request notification permission
    console.log('🔔 Requesting notification permission...')
    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      console.log('❌ Notification permission denied')
      return null
    }

    console.log('✅ Notification permission granted')

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      console.log('ℹ️ Already subscribed to push notifications')
      // Update subscription in database (in case it changed)
      await savePushSubscription(userId, subscription)
      return subscription
    }

    // Subscribe to push notifications
    console.log('📝 Creating new push subscription...')
    
    const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
    
    if (!vapidPublicKey) {
      console.error('❌ VAPID public key not configured')
      return null
    }

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    })

    console.log('✅ Push subscription created')

    // Save subscription to database
    await savePushSubscription(userId, subscription)

    return subscription

  } catch (error) {
    console.error('❌ Push subscription failed:', error)
    return null
  }
}

/**
 * Unsubscribe from push notifications
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} - Success status
 */
export async function unsubscribeFromPushNotifications(userId) {
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      await subscription.unsubscribe()
      console.log('✅ Unsubscribed from push notifications')
    }

    // Remove from database
    const { error } = await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId)

    if (error) throw error

    return true
  } catch (error) {
    console.error('❌ Failed to unsubscribe:', error)
    return false
  }
}

/**
 * Check if user is subscribed to push notifications
 * @returns {Promise<boolean>} - Subscription status
 */
export async function isPushSubscribed() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return false
    }

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    return !!subscription
  } catch (error) {
    console.error('Error checking push subscription:', error)
    return false
  }
}

/**
 * Get current push permission status
 * @returns {string} - 'granted', 'denied', or 'default'
 */
export function getPushPermissionStatus() {
  if (!('Notification' in window)) {
    return 'denied'
  }
  return Notification.permission
}

/**
 * Check notification status (permission + subscription)
 * @returns {Promise<{permission: string, subscribed: boolean, supported: boolean}>}
 */
export async function checkNotificationStatus() {
  try {
    // Check if notifications are supported
    const supported = 'Notification' in window && 
                     'serviceWorker' in navigator && 
                     'PushManager' in window

    if (!supported) {
      return {
        supported: false,
        permission: 'denied',
        subscribed: false
      }
    }

    // Get permission status
    const permission = Notification.permission

    // Check if subscribed
    const subscribed = await isPushSubscribed()

    return {
      supported: true,
      permission: permission, // 'granted', 'denied', or 'default'
      subscribed: subscribed
    }
  } catch (error) {
    console.error('Error checking notification status:', error)
    return {
      supported: false,
      permission: 'denied',
      subscribed: false
    }
  }
}

/**
 * Request notification permission from the user
 * @param {string} userId - The user's ID (optional, for auto-subscribe after permission)
 * @returns {Promise<string>} - Permission result: 'granted', 'denied', or 'default'
 */
export async function requestNotificationPermission(userId = null) {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.error('❌ Notifications not supported in this browser')
      return 'denied'
    }

    // If already granted, return immediately
    if (Notification.permission === 'granted') {
      console.log('✅ Notification permission already granted')
      
      // If userId provided and not subscribed, subscribe now
      if (userId) {
        const subscribed = await isPushSubscribed()
        if (!subscribed) {
          await subscribeToPushNotifications(userId)
        }
      }
      
      return 'granted'
    }

    // Request permission
    console.log('🔔 Requesting notification permission...')
    const permission = await Notification.requestPermission()

    if (permission === 'granted') {
      console.log('✅ Notification permission granted')
      
      // If userId provided, automatically subscribe
      if (userId) {
        await subscribeToPushNotifications(userId)
      }
    } else if (permission === 'denied') {
      console.log('❌ Notification permission denied')
    } else {
      console.log('⚠️ Notification permission dismissed')
    }

    return permission
  } catch (error) {
    console.error('❌ Error requesting notification permission:', error)
    return 'denied'
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Save push subscription to database
 * @private
 */
async function savePushSubscription(userId, subscription) {
  try {
    const subscriptionData = subscription.toJSON()

    const { error } = await supabase
      .from('push_subscriptions')
      .upsert({
        user_id: userId,
        endpoint: subscriptionData.endpoint,
        p256dh: subscriptionData.keys.p256dh,
        auth: subscriptionData.keys.auth,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,endpoint'
      })

    if (error) throw error

    console.log('✅ Push subscription saved to database')
  } catch (error) {
    console.error('❌ Failed to save subscription:', error)
    throw error
  }
}

/**
 * Convert VAPID key from base64 to Uint8Array
 * @private
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}